// Multi-target picker for the variation form. Layered:
//
//   1. Contract checkbox — toggles a `{ kind: 'contract' }` target in/out.
//   2. SOW selector — at 8 or fewer selectable SOWs, one chip button per
//      non-archived non-cancelled SOW on the contract, natural-sorted by
//      number (`05` before `14`, and `SOW-2026-002` before
//      `SOW-2026-010`) so the chip list reads like a SOW register rather
//      than a status-grouped jumble. Above 8 the chip wall is unscannable,
//      so it's replaced by a searchable checklist: a text filter (matches
//      SOW number or name, case-insensitively), a scrollable checkbox
//      list of the filtered SOWs, and a row of removable summary chips
//      for whatever is currently selected — so a selection hidden by the
//      active search term stays visible and removable. Both modes drive
//      the same `toggleSowTarget` / `clearAllocationsIfSingle` path.
//   3. Allocation rows — only when valueImpact is non-null AND more than
//      one target is selected. Rows render Contract-first then
//      SOW-number order (independent of click order) so the layout is
//      predictable regardless of how the user selected targets. Each
//      selected target gets a labelled <CurrencyInput> (signed, up to
//      2 decimal places, ex-GST). Because form state stores parsed
//      numbers, raws that don't survive the number→string round trip
//      (trailing "." or trailing zero cents) are held in a local draft
//      map so mid-decimal keystrokes stay visible — see
//      allocationRawNeedsDraft. Allocations are optional; when used, all selected
//      targets must be allocated and the sum must equal valueImpact
//      (validated upstream by the form). With a single target the
//      partition UI is meaningless (100% goes to that one target) and
//      is suppressed; toggle handlers also clear any stale allocation
//      when the list reduces to one entry.
//   4. Sum footer — only when more than one target is selected and at
//      least one allocation is non-empty. `Allocations sum: $X / $Y ·
//      $Z remaining`. Red when sum != total. `aria-live="polite"` so
//      screen-readers narrate sum changes.
//
// The picker is fully controlled — `value` is the canonical target list
// and every change is emitted via `onChange(nextValue)`. The parent
// integrates with `variationFormState.targets`.
//
// `disabled` freezes the whole picker, including allocations. `membershipLocked`
// freezes only membership (Contract/SOW chips, checklist checkboxes,
// summary-chip removes) while leaving allocation inputs live — used on
// an approved/rejected variation, where the target list is part of the
// audit trail but the allocation split still partitions the
// already-approved total.

import { useId, useMemo, useState } from "react";
import { X } from "lucide-react";
import CurrencyInput from "../shared/CurrencyInput";
import { SOW_SEARCH_THRESHOLD } from "../shared/sowPickerSearch";
import { gstSuffix } from "../../../../utils/gstSuffix";
import type { Sow, VariationTarget } from "../../../../types";
import {
  allocationSum,
  clearAllocationsIfSingle,
  hasAnyAllocation,
  setTargetAllocation,
  toggleContractTarget,
  toggleSowTarget,
} from "./variationTargets";

export interface VariationTargetPickerProps {
  value: VariationTarget[];
  onChange: (next: VariationTarget[]) => void;
  /** Full SOW list on the parent contract. The picker filters to
   *  non-archived non-cancelled at render time. */
  sows: Sow[];
  /** Total value impact in AUD whole dollars ex-GST, or null when the
   *  variation is non-financial. Drives the allocation footer + hides
   *  per-target allocation inputs entirely on a non-financial variation. */
  valueImpact: number | null;
  /** GST flag from the parent contract — drives the basis suffix on
   *  the allocation inputs. Optional; defaults to undefined which
   *  surfaces "GST status not recorded". */
  gstApplies?: boolean | undefined;
  disabled?: boolean;
  /** Freeze target membership (chips/checkboxes/removes) while keeping
   *  allocation inputs editable — used when editing an approved or
   *  rejected variation, whose membership is part of the audit trail
   *  but whose allocation split only partitions the already-approved
   *  total. */
  membershipLocked?: boolean;
  idPrefix: string;
}

const FORMATTER = new Intl.NumberFormat("en-AU", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function fmtAud(n: number): string {
  const sign = n < 0 ? "-" : "";
  return `${sign}$${FORMATTER.format(Math.abs(n))}`;
}

/** Above this many selectable SOWs the chip grid becomes a searchable
 *  checklist — a 30-chip wall is unscannable, and search-by-number is
 *  how users think about large SOW registers. Shared with the SOW
 *  filter/scope pickers so every SOW-search surface flips at the same
 *  register size. */
const CHECKLIST_THRESHOLD = SOW_SEARCH_THRESHOLD;

/** Sort: natural-ascending by SOW number (numeric-aware, so `05` < `14`
 *  and `SOW-2026-002` < `SOW-2026-010`). Number order matches how users
 *  scan a SOW register — with 30+ SOWs on a contract, status-grouped
 *  ordering reads as jumbled. Cancelled and archived SOWs excluded. */
function orderSows(sows: Sow[]): Sow[] {
  return [...sows]
    .filter((s) => s.status !== "cancelled" && !s.archivedAt)
    .sort((a, b) =>
      a.number.localeCompare(b.number, undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
}

function isContractSelected(value: VariationTarget[]): boolean {
  return value.some((t) => t.kind === "contract");
}

function isSowSelected(value: VariationTarget[], sowId: string): boolean {
  return value.some((t) => t.kind === "sow" && t.sowId === sowId);
}

function rawFromAllocation(allocation: number | null | undefined): string {
  if (allocation === null || allocation === undefined) return "";
  return String(allocation);
}

function parseAllocationRaw(raw: string): number | null {
  if (raw === "" || raw === "-" || raw === "+" || raw === ".") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  // Round to 2dp so the wire payload matches the backend monetaryAmount
  // invariant (≤2 decimal places).
  return Math.round(n * 100) / 100;
}

/** A raw input is "transient" when the user has typed a structural
 *  prefix (`-` or `+`) but not yet entered any digits. The form state
 *  only stores parseable numbers, so we hold the transient value
 *  locally in the picker and only emit to the parent once a digit
 *  lands. Without this carve-out, typing `-` in an empty allocation
 *  collapses to null on the round-trip and the user's `-` keystroke
 *  is silently dropped from the rendered input. */
function isTransientAllocationRaw(raw: string): boolean {
  return raw === "-" || raw === "+";
}

/** Form state stores allocations as parsed numbers, so any typed raw
 *  whose number→string round trip is lossy ("12." → 12 → "12", and
 *  likewise ".", "12.0", "12.50") would have its trailing keystrokes
 *  deleted by the re-render — making cents untypeable. Such raws are
 *  held in the local draft map (the parsed number still flows to form
 *  state, keeping the sum footer live) and the draft clears as soon as
 *  the raw survives the round trip again. */
function allocationRawNeedsDraft(raw: string): boolean {
  return raw !== "" && rawFromAllocation(parseAllocationRaw(raw)) !== raw;
}

/** Stable key per target for the local-draft map below. Contract has a
 *  single instance; sows are keyed by their id. */
function targetDraftKey(target: VariationTarget): string {
  return target.kind === "contract" ? "contract" : `sow:${target.sowId}`;
}

interface AllocationRowProps {
  label: string;
  testId: string;
  raw: string;
  basis: string;
  disabled: boolean;
  onChange: (raw: string) => void;
}

/** Allocation row layout uses an outer CSS grid (defined on the parent
 *  wrapper) so every label column has the same width and every input
 *  starts at the same x. The longest target label drives the column
 *  width via `min-content`; shorter labels pad to match. */
function AllocationRow({
  label,
  testId,
  raw,
  basis,
  disabled,
  onChange,
}: AllocationRowProps) {
  const id = useId();
  return (
    <div
      className="grid grid-cols-subgrid items-center gap-3"
      style={{ gridColumn: "1 / -1" }}
    >
      <label
        htmlFor={id}
        className="text-body-sm text-slate-700 dark:text-slate-200 truncate"
      >
        {label}
      </label>
      <div className="relative w-[180px]">
        <span
          aria-hidden="true"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400"
        >
          $
        </span>
        <CurrencyInput
          id={id}
          value={raw}
          onChange={onChange}
          allowNegative
          allowDecimal
          ariaLabel={`Allocation for ${label}`}
          disabled={disabled}
          placeholder="0"
          testId={testId}
        />
      </div>
      <span className="text-caption text-slate-500 dark:text-slate-400">
        {basis}
      </span>
    </div>
  );
}

export default function VariationTargetPicker({
  value,
  onChange,
  sows,
  valueImpact,
  gstApplies,
  disabled = false,
  membershipLocked = false,
  idPrefix,
}: VariationTargetPickerProps) {
  // Membership controls freeze on either a fully-disabled picker or a
  // decisioned-variation edit; allocation inputs only ever key off plain
  // `disabled` so the split stays editable in the latter case.
  const membershipDisabled = disabled || membershipLocked;
  const orderedSows = useMemo(() => orderSows(sows), [sows]);
  // Allocation rows render in display order — Contract first, then SOWs
  // by number — independent of the order targets were clicked. Stored
  // wire order is untouched; only the rendering sorts. Targets whose SOW
  // is not in `sows` at all (deleted) sort last by raw id.
  const displayTargets = useMemo(() => {
    const numberFor = (id: string) => sows.find((s) => s.id === id)?.number;
    return [...value].sort((a, b) => {
      // `value` carries at most one contract target (toggleContractTarget
      // dedupes it), so a tie between two contract entries never occurs.
      if (a.kind === "contract") return -1;
      if (b.kind === "contract") return 1;
      const na = numberFor(a.sowId);
      const nb = numberFor(b.sowId);
      if (na === undefined && nb === undefined)
        return a.sowId.localeCompare(b.sowId);
      if (na === undefined) return 1;
      if (nb === undefined) return -1;
      return na.localeCompare(nb, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
  }, [value, sows]);
  const [search, setSearch] = useState("");
  const useChecklist = orderedSows.length > CHECKLIST_THRESHOLD;
  const visibleSows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orderedSows;
    return orderedSows.filter(
      (s) =>
        s.number.toLowerCase().includes(q) ||
        s.name.toLowerCase().includes(q),
    );
  }, [orderedSows, search]);
  const selectedSowTargets = displayTargets.filter(
    (t): t is Extract<VariationTarget, { kind: "sow" }> => t.kind === "sow",
  );

  const contractChecked = isContractSelected(value);
  const basis = gstSuffix(gstApplies);
  // Hide the partition UI on a single target: 100% is implicit, so
  // surfacing an allocation row would only add noise.
  const showAllocations = valueImpact !== null && value.length > 1;

  const sum = allocationSum(value);
  const showFooter =
    valueImpact !== null && value.length > 1 && hasAnyAllocation(value);
  // Compare in cents — IEEE 754 makes 0.1 + 0.2 ≠ 0.3 in raw float math,
  // which would falsely flag a valid split as a mismatch.
  const mismatch =
    showFooter &&
    valueImpact !== null &&
    Math.round(sum * 100) !== Math.round(valueImpact * 100);
  const remaining = valueImpact === null ? 0 : valueImpact - sum;

  // Local drafts hold transient allocation strings (a lone `-` or `+`)
  // that don't parse to a number yet. These would otherwise round-trip
  // to null in form state and visually disappear from the input — see
  // isTransientAllocationRaw above for the rationale.
  const [transientDrafts, setTransientDrafts] = useState<
    Record<string, string>
  >({});

  const handleAllocationChange = (
    target: VariationTarget,
    raw: string,
  ) => {
    const key = targetDraftKey(target);
    if (isTransientAllocationRaw(raw)) {
      setTransientDrafts((prev) => ({ ...prev, [key]: raw }));
      // Form state stays unchanged — any existing allocation is kept
      // (and re-rendered by the guard in allocationRawFor once the
      // draft stops agreeing) until a digit lands and the parser
      // yields a real number.
      return;
    }
    if (allocationRawNeedsDraft(raw)) {
      // Mid-decimal typing ("12.", "12.50"): keep the typed raw visible
      // via the draft while the parsed number flows through below.
      setTransientDrafts((prev) => ({ ...prev, [key]: raw }));
    } else if (transientDrafts[key] !== undefined) {
      setTransientDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
    onChange(
      setTargetAllocation(value, target, parseAllocationRaw(raw)),
    );
  };

  const allocationRawFor = (target: VariationTarget): string => {
    const key = targetDraftKey(target);
    const draft = transientDrafts[key];
    if (draft !== undefined) {
      // Consistency guard: a draft is only trustworthy while it still
      // agrees with form state. Structural prefixes ("-"/"+") never
      // reach form state, so they always render. A decimal draft
      // ("12.50") renders only while it parses to the target's current
      // allocation — if the allocation was cleared or changed elsewhere
      // (e.g. clearAllocationsIfSingle on a membership toggle), the
      // stale draft would otherwise paint a filled-looking money field
      // over empty form state, letting the variation submit
      // unallocated with no validation error.
      if (
        isTransientAllocationRaw(draft) ||
        parseAllocationRaw(draft) === (target.allocation ?? null)
      ) {
        return draft;
      }
    }
    return rawFromAllocation(target.allocation);
  };

  const selectedClass =
    "inline-flex items-center gap-1.5 rounded-md border border-brand-500 bg-brand-50 px-2.5 py-1 text-body-sm text-brand-700 dark:border-brand-400 dark:bg-brand-900/30 dark:text-brand-200 disabled:opacity-50 disabled:cursor-not-allowed";
  const unselectedClass =
    "inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-body-sm text-slate-700 hover:border-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-slate-500 disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="space-y-3" data-testid={`${idPrefix}-target-picker`}>
      <span className="label">Targets</span>
      {membershipLocked && (
        <p className="text-caption text-slate-500 dark:text-slate-400">
          Targets and value impact are locked after approval; the
          allocation split can still be adjusted.
        </p>
      )}

      <div
        role="group"
        aria-label="Apply to"
        className="flex flex-wrap gap-2"
      >
        <button
          type="button"
          role="checkbox"
          aria-checked={contractChecked}
          onClick={() =>
            onChange(clearAllocationsIfSingle(toggleContractTarget(value)))
          }
          disabled={membershipDisabled}
          data-testid={`${idPrefix}-contract-chip`}
          className={contractChecked ? selectedClass : unselectedClass}
        >
          Contract
        </button>
        {!useChecklist &&
          orderedSows.map((s) => {
            const selected = isSowSelected(value, s.id);
            return (
              <button
                key={s.id}
                type="button"
                role="checkbox"
                aria-checked={selected}
                onClick={() =>
                  onChange(
                    clearAllocationsIfSingle(toggleSowTarget(value, s.id)),
                  )
                }
                disabled={membershipDisabled}
                data-testid={`${idPrefix}-sow-chip-${s.id}`}
                className={selected ? selectedClass : unselectedClass}
              >
                <span className="font-mono">{s.number}</span>
                <span className="text-slate-500 dark:text-slate-400">·</span>
                <span>{s.name}</span>
              </button>
            );
          })}
      </div>

      {useChecklist && (
        <div className="space-y-2">
          {selectedSowTargets.length > 0 && (
            <div className="flex flex-wrap gap-2" role="group" aria-label="Selected SOWs">
              {selectedSowTargets.map((t) => {
                const s = sows.find((x) => x.id === t.sowId);
                const label = s ? `${s.number} · ${s.name}` : t.sowId;
                return (
                  <span
                    key={t.sowId}
                    data-testid={`${idPrefix}-selected-chip-${t.sowId}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-brand-500 bg-brand-50 px-2.5 py-1 text-body-sm text-brand-700 dark:border-brand-400 dark:bg-brand-900/30 dark:text-brand-200"
                  >
                    {label}
                    <button
                      type="button"
                      aria-label={`Remove ${label}`}
                      disabled={membershipDisabled}
                      onClick={() =>
                        onChange(
                          clearAllocationsIfSingle(
                            toggleSowTarget(value, t.sowId),
                          ),
                        )
                      }
                      className="text-brand-500 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-100 disabled:opacity-50"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          <input
            type="search"
            className="input"
            placeholder="Search SOWs…"
            aria-label="Search SOWs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={disabled}
            data-testid={`${idPrefix}-sow-search`}
          />
          {visibleSows.length === 0 ? (
            <p
              className="text-caption text-slate-500 dark:text-slate-400"
              data-testid={`${idPrefix}-sow-search-empty`}
            >
              No SOWs match &ldquo;{search.trim()}&rdquo;.
            </p>
          ) : (
            <ul
              className="max-h-52 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800"
              data-testid={`${idPrefix}-sow-checklist`}
            >
              {visibleSows.map((s) => {
                const selected = isSowSelected(value, s.id);
                return (
                  <li key={s.id}>
                    <label className="flex items-center gap-2.5 px-3 py-2 text-body-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/60 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={membershipDisabled}
                        onChange={() =>
                          onChange(
                            clearAllocationsIfSingle(
                              toggleSowTarget(value, s.id),
                            ),
                          )
                        }
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400 dark:border-slate-600 dark:bg-slate-800"
                        data-testid={`${idPrefix}-sow-check-${s.id}`}
                      />
                      <span className="font-mono">{s.number}</span>
                      <span className="text-slate-500 dark:text-slate-400">·</span>
                      <span className="truncate">{s.name}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {orderedSows.length === 0 && (
        <p className="text-caption text-slate-500 dark:text-slate-400">
          No SOWs available — only the contract can be selected.
        </p>
      )}

      {showAllocations && (
        <div
          className="grid items-center gap-x-3 gap-y-2 pt-1"
          // Grid template: longest-label width drives column 1; the input
          // column is fixed-width; the basis suffix takes whatever's left.
          // `subgrid` on each row means every input lines up to the same
          // x even when one label is "Contract" and another is
          // "SOW-2026-001 · Customer Portal Build".
          style={{
            gridTemplateColumns: "max-content auto max-content",
          }}
          data-testid={`${idPrefix}-allocation-grid`}
        >
          <p
            className="text-caption text-slate-500 dark:text-slate-400"
            style={{ gridColumn: "1 / -1" }}
          >
            Optional. Partition the value impact across the selected
            targets, or leave blank to keep the variation unallocated.
          </p>
          {displayTargets.map((t) => {
            if (t.kind === "contract") {
              return (
                <AllocationRow
                  key="contract"
                  label="Contract"
                  testId={`${idPrefix}-allocation-contract`}
                  raw={allocationRawFor(t)}
                  basis={basis}
                  disabled={disabled}
                  onChange={(raw) => handleAllocationChange(t, raw)}
                />
              );
            }
            const sow = sows.find((s) => s.id === t.sowId);
            const label = sow ? `${sow.number} · ${sow.name}` : t.sowId;
            return (
              <AllocationRow
                // Stable per-target key. Earlier this used `${sowId}-${idx}`
                // which changes whenever a target is added or reordered,
                // remounting the input and losing CurrencyInput's internal
                // state mid-keystroke.
                key={`sow-${t.sowId}`}
                label={label}
                testId={`${idPrefix}-allocation-sow-${t.sowId}`}
                // Read through the transient-draft map so a lone leading
                // `-` typed into a SOW input is preserved between
                // keystrokes — matches the contract row above.
                raw={allocationRawFor(t)}
                basis={basis}
                disabled={disabled}
                onChange={(raw) => handleAllocationChange(t, raw)}
              />
            );
          })}
        </div>
      )}

      {showFooter && (
        <p
          aria-live="polite"
          data-testid={`${idPrefix}-allocation-sum`}
          className={
            mismatch
              ? "text-caption text-red-600 dark:text-red-400"
              : "text-caption text-slate-600 dark:text-slate-300"
          }
        >
          Allocations sum: {fmtAud(sum)} / {fmtAud(valueImpact ?? 0)}
          {remaining !== 0 ? ` · ${fmtAud(remaining)} remaining` : ""}
        </p>
      )}
    </div>
  );
}
